import { useState, useEffect } from 'preact/hooks';
import { apiGet, apiPost, apiPut, apiDelete } from '../lib/api';
import { ServerCard, type Server, type Service } from '../components/ServerCard';
import { MessagePreview } from '../components/MessagePreview';

interface MessageTemplate {
  id: string;
  template: string;
  description: string | null;
}

type View = 'servers' | 'messages';

export function Config() {
  const [servers, setServers] = useState<Server[]>([]);
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [view, setView] = useState<View>('servers');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Track original state for dirty detection
  const [originalServers, setOriginalServers] = useState<Server[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [serversData, templatesData] = await Promise.all([
        apiGet<Server[]>('/config/servers'),
        apiGet<MessageTemplate[]>('/config/messages'),
      ]);
      setServers(serversData);
      setOriginalServers(JSON.parse(JSON.stringify(serversData)));
      setTemplates(templatesData);
      setError('');
    } catch (e) {
      setError('Fehler beim Laden der Konfiguration.');
    } finally {
      setLoading(false);
    }
  };

  const handleServicesChange = (serverId: string, updatedServices: Service[]) => {
    setServers((prev) =>
      prev.map((s) => (s.id === serverId ? { ...s, services: updatedServices } : s))
    );
    setDirty(true);
  };

  const handleAddService = (serverId: string) => {
    const newService: Service = {
      id: `new-${crypto.randomUUID()}`,
      name: '',
      container_name: null,
      compose_dir: null,
      group_name: null,
      sort_order: servers.find((s) => s.id === serverId)?.services.length ?? 0,
      is_restartable: 0,
    };
    setServers((prev) =>
      prev.map((s) =>
        s.id === serverId ? { ...s, services: [...s.services, newService] } : s
      )
    );
    setDirty(true);
  };

  const handleSaveAll = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      for (const server of servers) {
        const originalServer = originalServers.find((s) => s.id === server.id);
        const originalIds = new Set(originalServer?.services.map((s) => s.id) ?? []);
        const currentIds = new Set(server.services.map((s) => s.id));

        // Deleted services
        for (const id of originalIds) {
          if (!currentIds.has(id)) {
            await apiDelete('/config/services?id=' + id);
          }
        }

        // New services (IDs starting with "new-")
        for (const svc of server.services) {
          if (svc.id.startsWith('new-')) {
            if (!svc.name.trim()) continue; // skip empty placeholder services
            await apiPost('/config/services', {
              server_id: server.id,
              name: svc.name,
              container_name: svc.container_name,
              compose_dir: svc.compose_dir,
              group_name: svc.group_name,
              sort_order: svc.sort_order,
              is_restartable: !!svc.is_restartable,
            });
          }
        }

        // Updated services (existing IDs that aren't new)
        for (const svc of server.services) {
          if (svc.id.startsWith('new-')) continue;
          if (!originalIds.has(svc.id)) continue;

          const original = originalServer?.services.find((s) => s.id === svc.id);
          if (!original) continue;

          // Check if changed
          const changed =
            svc.name !== original.name ||
            svc.group_name !== original.group_name ||
            svc.sort_order !== original.sort_order ||
            svc.compose_dir !== original.compose_dir ||
            svc.container_name !== original.container_name ||
            svc.is_restartable !== original.is_restartable;

          if (changed) {
            await apiPut('/config/services', {
              id: svc.id,
              name: svc.name,
              container_name: svc.container_name,
              compose_dir: svc.compose_dir,
              group_name: svc.group_name,
              sort_order: svc.sort_order,
              is_restartable: !!svc.is_restartable,
            });
          }
        }
      }

      // Reload to get fresh data (server-assigned IDs for new services)
      await loadData();
      setDirty(false);
      setSuccess('Konfiguration gespeichert.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError('Fehler beim Speichern.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTemplate = async (id: string, template: string) => {
    try {
      await apiPut('/config/messages', { id, template });
      setTemplates((prev) =>
        prev.map((t) => (t.id === id ? { ...t, template } : t))
      );
      setSuccess('Template gespeichert.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError('Fehler beim Speichern des Templates.');
    }
  };

  if (loading) {
    return (
      <div class="page">
        <h1>Konfiguration</h1>
        <p>Laden...</p>
      </div>
    );
  }

  return (
    <div class="page">
      <div class="config-header">
        <h1>Konfiguration</h1>
        <div class="tab-bar">
          <button
            class={`tab ${view === 'servers' ? 'tab-active' : ''}`}
            onClick={() => setView('servers')}
          >
            Server & Services
          </button>
          <button
            class={`tab ${view === 'messages' ? 'tab-active' : ''}`}
            onClick={() => setView('messages')}
          >
            Nachrichten
          </button>
        </div>
      </div>

      {error && <div class="alert alert-error">{error}</div>}
      {success && <div class="alert alert-success">{success}</div>}

      {view === 'servers' && (
        <div class="config-servers">
          {servers.map((server) => (
            <ServerCard
              key={server.id}
              server={server}
              onServicesChange={handleServicesChange}
              onAddService={handleAddService}
            />
          ))}

          {dirty && (
            <div class="save-bar">
              <button
                class="btn btn-primary btn-save"
                onClick={handleSaveAll}
                disabled={saving}
              >
                {saving ? 'Speichern...' : 'Änderungen speichern'}
              </button>
              <button
                class="btn btn-secondary"
                onClick={() => {
                  setServers(JSON.parse(JSON.stringify(originalServers)));
                  setDirty(false);
                }}
                disabled={saving}
              >
                Verwerfen
              </button>
            </div>
          )}
        </div>
      )}

      {view === 'messages' && (
        <div class="config-messages">
          {templates.map((tpl) => (
            <MessagePreview key={tpl.id} template={tpl} onSave={handleSaveTemplate} />
          ))}
        </div>
      )}
    </div>
  );
}
